// src/features/business/pages/BusinessesPage.tsx
// ENHANCED UI/UX - ALL FUNCTIONALITY PRESERVED
// ✅ FIXED: Business cards/table now show branch & staff counts from stats endpoint

import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import * as businessApi from '../services/businessApi';
import type { Business } from '../services/businessApi';
import {
    Building2, Plus, Search, LayoutGrid, List, ChevronLeft, ChevronRight,
    Store, Users as UsersIcon, CheckCircle2, Pencil, Trash2,
    ArrowUpDown, Eye, ArrowLeft, TrendingUp, Sparkles,
    Layers, Briefcase, Target, DollarSign, X, Save, Globe,
    Truck, MapPin, Phone, Mail, Clock, Star, Zap, ChevronsLeft,
    ChevronsRight, Filter, RefreshCw, MoreHorizontal, ExternalLink,
    Rocket, PartyPopper, BadgeCheck, Palette,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import React from 'react';

// ============================================================
// CONSTANTS
// ============================================================

const businessTypes: Record<string, { label: string; emoji: string; icon: React.ElementType; gradient: string; color: string }> = {
    retail: { label: 'Retail Store', emoji: '🏪', icon: Store, gradient: 'from-blue-500 to-indigo-600', color: '#3B82F6' },
    restaurant: { label: 'Restaurant & Bar', emoji: '🍽️', icon: Store, gradient: 'from-amber-500 to-orange-600', color: '#F59E0B' },
    wholesale: { label: 'Wholesale', emoji: '📦', icon: Truck, gradient: 'from-purple-500 to-violet-600', color: '#8B5CF6' },
    pharmacy: { label: 'Pharmacy', emoji: '💊', icon: Store, gradient: 'from-emerald-500 to-teal-600', color: '#10B981' },
    service: { label: 'Service Business', emoji: '🔧', icon: Briefcase, gradient: 'from-rose-500 to-pink-600', color: '#F43F5E' },
};

const PAGE_SIZE_OPTIONS = [12, 24, 48];

// ============================================================
// FORMATTERS
// ============================================================

const formatDate = (date: string) => {
    try { return new Date(date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return 'N/A'; }
};

const formatTimeAgo = (date: string) => {
    try {
        const now = new Date();
        const created = new Date(date);
        const diffMs = now.getTime() - created.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return formatDate(date);
    } catch { return 'N/A'; }
};

const EMPTY_FORM = {
    name: '', shortCode: '', type: 'retail', description: '',
    brandColor: '#0D9488', contactEmail: '', contactPhone: '', website: '', address: ''
};

// ============================================================
// CELEBRATION CONFETTI
// ============================================================

function CelebrationOverlay({ show, businessName, onComplete }: { show: boolean; businessName: string; onComplete: () => void }) {
    useEffect(() => {
        if (show) {
            const timer = setTimeout(onComplete, 3000);
            return () => clearTimeout(timer);
        }
    }, [show, onComplete]);

    if (!show) return null;

    const confettiColors = ['#10B981', '#06B6D4', '#8B5CF6', '#F59E0B', '#EC4899', '#3B82F6', '#EF4444'];
    const confettiPieces = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        delay: Math.random() * 2,
        duration: 1.5 + Math.random() * 2,
        color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
        size: 8 + Math.random() * 12,
        rotation: Math.random() * 360,
    }));

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] pointer-events-none overflow-hidden"
            >
                {/* Confetti */}
                {confettiPieces.map(piece => (
                    <motion.div
                        key={piece.id}
                        initial={{ y: -20, x: 0, opacity: 1, rotate: 0 }}
                        animate={{ y: '100vh', x: `${(Math.random() - 0.5) * 200}px`, opacity: 0, rotate: piece.rotation + 360 }}
                        transition={{ duration: piece.duration, delay: piece.delay, ease: 'easeIn' }}
                        className="absolute top-0 rounded-sm"
                        style={{
                            left: piece.left,
                            width: piece.size,
                            height: piece.size * 0.6,
                            backgroundColor: piece.color,
                        }}
                    />
                ))}

                {/* Center celebration card */}
                <motion.div
                    initial={{ scale: 0, y: -50 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0, y: -50 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
                >
                    <div className="bg-white rounded-3xl shadow-2xl p-8 text-center border-2 border-emerald-200">
                        <motion.div
                            animate={{ rotate: [0, -10, 10, -10, 0] }}
                            transition={{ duration: 0.8, repeat: Infinity, repeatDelay: 1 }}
                            className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-200"
                        >
                            <PartyPopper size={40} className="text-white" />
                        </motion.div>
                        <h2 className="text-2xl font-black text-slate-900 mb-1">🎉 Business Created!</h2>
                        <p className="text-slate-500 font-medium">
                            <strong className="text-emerald-600">{businessName}</strong> is ready to go!
                        </p>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

// ============================================================
// ENHANCED BUSINESS CARD
// ============================================================

function BusinessCard({ business, onEdit, onDelete, isNew }: {
    business: Business;
    onEdit: (b: Business) => void;
    onDelete: (b: Business) => void;
    isNew?: boolean;
}) {
    const navigate = useNavigate();
    const typeConfig = businessTypes[business.type] || businessTypes.retail;
    const brandColor = business.brandColor || '#0D9488';
    const branchCount = (business as any).branchCount || 0;
    const staffCount = (business as any).staffCount || 0;

    return (
        <motion.div
            initial={isNew ? { opacity: 0, y: 20, scale: 0.95 } : { opacity: 0, y: 20 }}
            animate={isNew ? { opacity: 1, y: 0, scale: 1 } : { opacity: 1, y: 0 }}
            transition={isNew ? { type: 'spring', stiffness: 300, damping: 20, delay: 0.1 } : { duration: 0.3 }}
            whileHover={{ y: -6, scale: 1.02 }}
            className={clsx(
                'group bg-white rounded-2xl border-2 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer flex flex-col relative',
                isNew ? 'border-emerald-400 ring-2 ring-emerald-200 ring-offset-2' : 'border-slate-200'
            )}
        >
            {/* New Badge */}
            {isNew && (
                <motion.div
                    initial={{ x: 100 }}
                    animate={{ x: 0 }}
                    transition={{ delay: 0.5, type: 'spring' }}
                    className="absolute top-3 right-3 z-20"
                >
                    <Badge className="bg-emerald-500 text-white border-emerald-400 shadow-lg shadow-emerald-200 font-bold px-3 py-1">
                        <Sparkles size={12} className="mr-1 inline" /> New
                    </Badge>
                </motion.div>
            )}

            {/* Brand Color Header */}
            <div className="h-2.5 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${brandColor}, ${brandColor}cc, ${brandColor}99)` }}>
                <div className="absolute inset-0 bg-white/10" />
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20" />
            </div>

            <div className="p-5 flex flex-col flex-1">
                {/* Header: Avatar + Status */}
                <div className="flex items-start justify-between mb-4">
                    <motion.div
                        whileHover={{ scale: 1.1, rotate: -3 }}
                        className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-black shadow-lg flex-shrink-0 relative overflow-hidden"
                        style={{ backgroundColor: brandColor }}
                    >
                        <div className="absolute inset-0 bg-white/10" />
                        <span className="relative z-10">{business.name.charAt(0).toUpperCase()}</span>
                    </motion.div>
                    <div className="flex flex-col items-end gap-1.5">
                        <Badge variant={business.isActive ? 'success' : 'danger'} size="sm" className="font-medium">
                            {business.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge
                            variant="neutral"
                            size="sm"
                            className="font-medium"
                            style={{
                                backgroundColor: `${typeConfig.color}15`,
                                color: typeConfig.color,
                                borderColor: `${typeConfig.color}30`
                            }}
                        >
                            {typeConfig.emoji} {typeConfig.label}
                        </Badge>
                    </div>
                </div>

                {/* Name & Code */}
                <h3
                    className="font-bold text-slate-900 text-base mb-1 cursor-pointer hover:text-emerald-600 transition-colors truncate"
                    onClick={() => navigate(`/businesses/${business.id}`)}
                >
                    {business.name}
                </h3>
                <code className="text-xs text-slate-400 font-mono bg-slate-100 px-2 py-0.5 rounded-md w-fit mb-3">
                    {business.shortCode}
                </code>

                {/* Contact Info */}
                <div className="space-y-1.5 mb-4">
                    {business.contactEmail && (
                        <p className="text-xs text-slate-500 flex items-center gap-1.5">
                            <Mail size={10} className="text-slate-400 flex-shrink-0" />
                            <span className="truncate">{business.contactEmail}</span>
                        </p>
                    )}
                    {business.contactPhone && (
                        <p className="text-xs text-slate-500 flex items-center gap-1.5">
                            <Phone size={10} className="text-slate-400 flex-shrink-0" />
                            <span>{business.contactPhone}</span>
                        </p>
                    )}
                    {business.address && (
                        <p className="text-xs text-slate-400 flex items-center gap-1.5">
                            <MapPin size={10} className="text-slate-300 flex-shrink-0" />
                            <span className="truncate">{business.address}</span>
                        </p>
                    )}
                </div>

                <div className="flex-1" />

                {/* Stats Row */}
                <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-slate-50 rounded-xl">
                    <div className="text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                            <Store size={12} className="text-teal-500" />
                            <span className="text-lg font-black text-slate-700">{branchCount}</span>
                        </div>
                        <p className="text-[10px] text-slate-400 font-medium uppercase">Branches</p>
                    </div>
                    <div className="text-center border-l border-slate-200">
                        <div className="flex items-center justify-center gap-1 mb-1">
                            <UsersIcon size={12} className="text-purple-500" />
                            <span className="text-lg font-black text-slate-700">{staffCount}</span>
                        </div>
                        <p className="text-[10px] text-slate-400 font-medium uppercase">Staff</p>
                    </div>
                </div>

                {/* Footer: Date + Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                        <Clock size={10} />
                        {formatTimeAgo(business.createdAt)}
                    </span>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all duration-200">
                        <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/businesses/${business.id}`); }}
                            className="p-1.5 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-all hover:scale-110"
                            title="View Details"
                        >
                            <Eye size={14} />
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onEdit(business); }}
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-all hover:scale-110"
                            title="Edit Business"
                        >
                            <Pencil size={14} />
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onDelete(business); }}
                            className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-all hover:scale-110"
                            title="Deactivate Business"
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

// ============================================================
// ENHANCED STAT CARD
// ============================================================

function StatCard({ label, value, icon: Icon, gradient, trend, sublabel }: {
    label: string; value: number | string; icon: React.ElementType; gradient: string; trend?: 'up' | 'down'; sublabel?: string;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4, scale: 1.02 }}
            className="relative overflow-hidden bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all duration-300 group"
        >
            <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl ${gradient} opacity-[0.06] rounded-bl-full group-hover:opacity-[0.12] transition-opacity`} />
            <div className="relative">
                <div className="flex items-start justify-between mb-3">
                    <div className="space-y-1">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
                        <p className="text-2xl lg:text-3xl font-black text-slate-900 tracking-tight">
                            {typeof value === 'number' ? value.toLocaleString() : value}
                        </p>
                        {sublabel && <p className="text-[11px] text-slate-400 font-medium">{sublabel}</p>}
                    </div>
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-lg group-hover:shadow-xl transition-shadow`}>
                        <Icon className="w-5 h-5 text-white" />
                    </div>
                </div>
                {trend && (
                    <div className="flex items-center gap-1.5">
                        <div className={clsx('p-1 rounded-full', trend === 'up' ? 'bg-emerald-100' : 'bg-rose-100')}>
                            <TrendingUp size={10} className={clsx(trend === 'up' ? 'text-emerald-600' : 'text-rose-600', trend === 'down' && 'rotate-180')} />
                        </div>
                        <span className={clsx('text-[10px] font-semibold', trend === 'up' ? 'text-emerald-600' : 'text-rose-600')}>
                            Active businesses
                        </span>
                    </div>
                )}
            </div>
        </motion.div>
    );
}

// ============================================================
// PAGINATION
// ============================================================

function Pagination({ currentPage, totalPages, totalItems, pageSize, onPageChange, onPageSizeChange }: {
    currentPage: number; totalPages: number; totalItems: number; pageSize: number;
    onPageChange: (page: number) => void; onPageSizeChange: (size: number) => void;
}) {
    if (totalItems === 0) return null;

    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    const pageNumbers: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
        pageNumbers.push(1);
        if (currentPage > 3) pageNumbers.push('ellipsis');
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);
        for (let i = start; i <= end; i++) pageNumbers.push(i);
        if (currentPage < totalPages - 2) pageNumbers.push('ellipsis');
        pageNumbers.push(totalPages);
    }

    return (
        <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
                <p className="text-sm text-slate-600">
                    Showing <span className="font-semibold text-slate-900">{startItem}</span> to{' '}
                    <span className="font-semibold text-slate-900">{endItem}</span> of{' '}
                    <span className="font-semibold text-slate-900">{totalItems.toLocaleString()}</span>
                </p>
                <select
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 border border-slate-200 rounded-lg text-xs bg-white text-slate-600"
                >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>{size} per page</option>
                    ))}
                </select>
            </div>
            <div className="flex gap-2 flex-wrap">
                <button onClick={() => onPageChange(1)} disabled={currentPage === 1} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 disabled:opacity-30" type="button">
                    <ChevronsLeft className="w-4 h-4" />
                </button>
                <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 font-medium transition flex items-center gap-1" type="button">
                    <ChevronLeft className="w-4 h-4" /> Prev
                </button>
                {pageNumbers.map((page, idx) => (
                    <React.Fragment key={idx}>
                        {page === 'ellipsis' ? (
                            <span className="px-2 py-2 text-slate-400">...</span>
                        ) : (
                            <button
                                onClick={() => onPageChange(page as number)}
                                className={clsx(
                                    'px-4 py-2 rounded-xl text-sm font-medium transition',
                                    currentPage === page
                                        ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-md shadow-teal-500/25'
                                        : 'border border-slate-200 hover:bg-slate-100 text-slate-700'
                                )}
                                type="button"
                            >
                                {page}
                            </button>
                        )}
                    </React.Fragment>
                ))}
                <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages} className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 font-medium transition flex items-center gap-1" type="button">
                    Next <ChevronRight className="w-4 h-4" />
                </button>
                <button onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 disabled:opacity-30" type="button">
                    <ChevronsRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

// ============================================================
// SKELETON LOADER
// ============================================================

function CardSkeleton() {
    return (
        <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden animate-pulse">
            <div className="h-2 bg-slate-200" />
            <div className="p-5 space-y-3">
                <div className="flex items-start justify-between">
                    <div className="w-14 h-14 rounded-2xl bg-slate-200" />
                    <div className="space-y-2">
                        <div className="w-16 h-5 rounded-full bg-slate-200" />
                        <div className="w-20 h-5 rounded-full bg-slate-200" />
                    </div>
                </div>
                <div className="w-3/4 h-5 rounded bg-slate-200" />
                <div className="w-1/3 h-4 rounded bg-slate-200" />
                <div className="space-y-2">
                    <div className="w-1/2 h-3 rounded bg-slate-200" />
                    <div className="w-2/3 h-3 rounded bg-slate-200" />
                </div>
                <div className="grid grid-cols-2 gap-3 p-3 bg-slate-100 rounded-xl">
                    <div className="space-y-1 text-center">
                        <div className="w-8 h-4 rounded bg-slate-200 mx-auto" />
                        <div className="w-12 h-3 rounded bg-slate-200 mx-auto" />
                    </div>
                    <div className="space-y-1 text-center">
                        <div className="w-8 h-4 rounded bg-slate-200 mx-auto" />
                        <div className="w-12 h-3 rounded bg-slate-200 mx-auto" />
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function BusinessesPage() {
    const navigate = useNavigate();
    const tenantId = useAuthStore(s => s.user?.tenantId);
    const user = useAuthStore(s => s.user);
    const tenant = useAuthStore(s => s.tenant);
    const queryClient = useQueryClient();
    const addToast = useToastStore(s => s.addToast);

    const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');

    const [showForm, setShowForm] = useState(false);
    const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [deleteConfirm, setDeleteConfirm] = useState<Business | null>(null);

    // Celebration state
    const [showCelebration, setShowCelebration] = useState(false);
    const [newBusinessName, setNewBusinessName] = useState('');
    const [newBusinessId, setNewBusinessId] = useState<string | null>(null);

    // Scroll to top when new business is created
    const listRef = useRef<HTMLDivElement>(null);

    // ==================== QUERIES ====================

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['businesses', tenantId, page, search, sortBy, order, pageSize],
        queryFn: () => businessApi.getBusinesses({ limit: pageSize, page, search, sortBy, order }),
        enabled: !!tenantId,
    });

    const { data: statsData } = useQuery({
        queryKey: ['businesses-stats', tenantId],
        queryFn: businessApi.getBusinessesWithStats,
        enabled: !!tenantId,
    });

    // ==================== DERIVED DATA ====================

    const businesses = data?.businesses || [];
    const totalCount = data?.totalCount || 0;
    const totalPages = Math.ceil(totalCount / pageSize);
    const stats = statsData || [];

    // ✅ FIX: Enrich businesses with branchCount and staffCount from stats endpoint
    const enrichedBusinesses = useMemo(() => {
        const statsMap = new Map(stats.map((b: any) => [b.id, b]));
        return businesses.map(b => {
            const stat = statsMap.get(b.id) as any;
            return {
                ...b,
                branchCount: stat?.branchCount ?? (b as any).branchCount ?? 0,
                staffCount: stat?.staffCount ?? (b as any).staffCount ?? 0,
            };
        });
    }, [businesses, stats]);

    const totalBranches = stats.reduce((s, b) => s + ((b as any).branchCount || 0), 0);
    const totalStaff = stats.reduce((s, b) => s + ((b as any).staffCount || 0), 0);
    const activeCount = stats.filter(b => b.isActive).length;

    // ==================== MUTATIONS ====================

    const invalidateCache = () => {
        queryClient.invalidateQueries({ queryKey: ['businesses'] });
        queryClient.invalidateQueries({ queryKey: ['businesses-stats'] });
    };

    const saveMutation = useMutation({
        mutationFn: async () => {
            const payload: Record<string, unknown> = {
                name: form.name.trim(),
                shortCode: form.shortCode.toUpperCase().trim(),
                type: form.type
            };
            if (form.description.trim()) payload.description = form.description.trim();
            if (form.brandColor && form.brandColor !== '#0D9488') payload.brandColor = form.brandColor.trim();
            if (form.contactEmail.trim()) payload.contactEmail = form.contactEmail.trim();
            if (form.contactPhone.trim()) payload.contactPhone = form.contactPhone.trim();
            if (form.website.trim()) payload.website = form.website.trim();
            if (form.address.trim()) payload.address = form.address.trim();

            if (editingBusiness) return businessApi.updateBusiness(editingBusiness.id, payload);
            return businessApi.createBusiness(payload as any);
        },
        onSuccess: (data) => {
            invalidateCache();
            const businessName = (data as any).name || form.name;
            const businessId = (data as any).id;

            addToast({
                type: 'success',
                title: editingBusiness ? 'Business Updated ✨' : 'Business Created! 🎉',
                message: editingBusiness
                    ? `${businessName} has been updated successfully.`
                    : `${businessName} is ready. Start adding branches and accounts!`,
                action: !editingBusiness ? {
                    label: 'View Business',
                    onClick: () => navigate(`/businesses/${businessId}`)
                } : undefined
            });

            handleCloseForm();

            // ✅ Show celebration and mark as new (stay on same page)
            if (!editingBusiness) {
                setNewBusinessName(businessName);
                setNewBusinessId(businessId);
                setShowCelebration(true);

                // Reset to page 1 to show the new business at top
                setPage(1);
                // Scroll to top
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
        onError: (err: any) => {
            const responseData = err?.response?.data;
            const errorDetails = responseData?.error;

            // Handle ValidationError with details array from backend
            if (errorDetails?.details && Array.isArray(errorDetails.details)) {
                const fieldErrors: Record<string, string> = {};

                errorDetails.details.forEach((detail: { field: string; message: string; code?: string }) => {
                    const fieldName = detail.field;
                    if (fieldName === 'name' || fieldName === 'shortCode' || fieldName === 'type' ||
                        fieldName === 'description' || fieldName === 'contactEmail' || fieldName === 'contactPhone' ||
                        fieldName === 'website' || fieldName === 'address' || fieldName === 'brandColor') {
                        fieldErrors[fieldName] = detail.message;
                    }
                });

                setFormErrors(fieldErrors);

                const firstError = errorDetails.details[0];
                addToast({
                    type: 'error',
                    title: 'Validation Error',
                    message: firstError?.message || 'Please check the form for errors.'
                });
                return;
            }

            const msg = errorDetails?.message || errorDetails || 'Failed to save business.';

            if (typeof msg === 'string') {
                if (msg.toLowerCase().includes('short code') || msg.toLowerCase().includes('shortcode')) {
                    setFormErrors({ shortCode: msg });
                } else if (msg.toLowerCase().includes('name')) {
                    setFormErrors({ name: msg });
                }
            }

            addToast({
                type: 'error',
                title: 'Save Failed',
                message: typeof msg === 'string' ? msg : 'An unexpected error occurred.'
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => businessApi.deactivateBusiness(id),
        onSuccess: () => {
            invalidateCache();
            addToast({ type: 'success', title: 'Business Deactivated', message: 'It can be reactivated from settings.' });
            setDeleteConfirm(null);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
    });

    // ==================== HELPERS ====================

    const updateFormField = (field: keyof typeof EMPTY_FORM, value: string) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (formErrors[field]) {
            setFormErrors(prev => {
                const updated = { ...prev };
                delete updated[field];
                return updated;
            });
        }
    };

    const handleEdit = (b: Business) => {
        setEditingBusiness(b);
        setForm({
            name: b.name, shortCode: b.shortCode, type: b.type,
            description: b.description || '', brandColor: b.brandColor || '#0D9488',
            contactEmail: b.contactEmail || '', contactPhone: b.contactPhone || '',
            website: b.website || '', address: b.address || ''
        });
        setFormErrors({});
        setShowForm(true);
    };

    const handleCloseForm = () => { setShowForm(false); setEditingBusiness(null); setForm(EMPTY_FORM); setFormErrors({}); };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};
        if (!form.name.trim()) errors.name = 'Business name is required';
        if (!form.shortCode.trim()) errors.shortCode = 'Short code is required';
        else if (form.shortCode.trim().length < 2) errors.shortCode = 'Short code must be at least 2 characters';
        else if (!/^[A-Z0-9]+$/.test(form.shortCode.trim().toUpperCase())) errors.shortCode = 'Only uppercase letters and numbers';
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            addToast({ type: 'warning', title: 'Form Incomplete', message: 'Please fix the highlighted fields.' });
            return false;
        }
        return true;
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        setPage(1);
    };

    // ==================== LOADING ====================

    if (isLoading) return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">
                {/* Header skeleton */}
                <div className="rounded-3xl bg-slate-200 animate-pulse h-40" />
                {/* Stats skeleton */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 animate-pulse h-28" />
                    ))}
                </div>
                {/* Cards skeleton */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {[...Array(8)].map((_, i) => (
                        <CardSkeleton key={i} />
                    ))}
                </div>
            </div>
        </div>
    );

    if (isError) return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <ErrorState title="Failed to load businesses" onRetry={() => refetch()} />
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">

                {/* ========== CELEBRATION OVERLAY ========== */}
                <CelebrationOverlay
                    show={showCelebration}
                    businessName={newBusinessName}
                    onComplete={() => setShowCelebration(false)}
                />

                {/* ========== HEADER ========== */}
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-teal-600 mb-3 group transition-colors"
                    >
                        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                        Back to Dashboard
                    </button>

                    <div className="relative overflow-hidden bg-gradient-to-br from-teal-500 via-emerald-600 to-teal-700 rounded-3xl shadow-2xl">
                        <div className="absolute inset-0">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.15),transparent_50%)]" />
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
                            <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gradient-to-tr from-teal-400/20 to-transparent rounded-full blur-3xl" />
                        </div>
                        <div className="relative px-6 lg:px-10 py-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <motion.div
                                    whileHover={{ rotate: -5, scale: 1.05 }}
                                    className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm ring-1 ring-white/20 flex items-center justify-center shadow-xl"
                                >
                                    <Building2 size={32} className="text-white" />
                                </motion.div>
                                <div>
                                    <h1 className="text-3xl lg:text-4xl font-black text-white tracking-tight">
                                        {user?.tenantId ? 'Businesses' : 'Your Businesses'}
                                    </h1>
                                    <p className="text-teal-100 text-sm mt-1 font-medium">
                                        {totalCount} business{totalCount !== 1 ? 'es' : ''} · {activeCount} active · {totalBranches} branches · {totalStaff} staff
                                    </p>
                                </div>
                            </div>
                            {tenant?.businessModel === 'single' && totalCount >= 1 ? (
                                <div className="px-4 py-2.5 rounded-2xl bg-white/20 backdrop-blur-sm text-white text-xs font-semibold border border-white/30 flex items-center gap-1.5">
                                    <span>Single Business Plan (1/1)</span>
                                </div>
                            ) : (
                                <motion.div
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                >
                                    <Button
                                        size="md"
                                        className="!bg-white !text-emerald-700 hover:!text-emerald-800 !font-bold !px-6 !py-3 !rounded-2xl !text-base !border-2 !border-white/80 !shadow-xl !shadow-black/20 hover:!shadow-black/30 hover:!bg-emerald-50 transition-all duration-300"
                                        leftIcon={<Plus size={18} />}
                                        onClick={() => { handleCloseForm(); setShowForm(true); }}
                                    >
                                        Add Business
                                    </Button>
                                </motion.div>
                            )}
                        </div>
                    </div>
                </motion.div>

                {/* ========== STATS ROW ========== */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard label="Total Businesses" value={totalCount} icon={Building2} gradient="from-teal-500 to-teal-700" trend="up" sublabel={`${activeCount} active`} />
                    <StatCard label="Active" value={activeCount} icon={BadgeCheck} gradient="from-emerald-500 to-emerald-700" sublabel={`${totalCount > 0 ? Math.round((activeCount / totalCount) * 100) : 0}% of total`} />
                    <StatCard label="Branches" value={totalBranches} icon={Store} gradient="from-blue-500 to-indigo-600" />
                    <StatCard label="Staff" value={totalStaff} icon={UsersIcon} gradient="from-purple-500 to-violet-600" />
                </div>

                {/* ========== TOOLBAR ========== */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden sticky top-4 z-30 backdrop-blur-xl bg-white/95">
                    <div className="p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            {/* Search */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search businesses..."
                                    value={search}
                                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                                    className="pl-9 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm w-56 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 focus:bg-white transition-all"
                                />
                                {search && (
                                    <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:bg-slate-200 text-slate-400">
                                        <X size={12} />
                                    </button>
                                )}
                            </div>

                            {/* View Mode */}
                            <div className="flex items-center bg-slate-100 rounded-xl p-1">
                                <button
                                    onClick={() => setViewMode('card')}
                                    className={clsx('flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all',
                                        viewMode === 'card' ? 'bg-white shadow-sm text-teal-600' : 'text-slate-500 hover:text-slate-700')}
                                >
                                    <LayoutGrid size={14} /> Cards
                                </button>
                                <button
                                    onClick={() => setViewMode('table')}
                                    className={clsx('flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all',
                                        viewMode === 'table' ? 'bg-white shadow-sm text-teal-600' : 'text-slate-500 hover:text-slate-700')}
                                >
                                    <List size={14} /> Table
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            {/* Sort */}
                            <select
                                value={sortBy}
                                onChange={e => { setSortBy(e.target.value); setPage(1); }}
                                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-600"
                            >
                                <option value="createdAt">Date Created</option>
                                <option value="name">Name</option>
                                <option value="type">Type</option>
                            </select>
                            <button
                                onClick={() => setOrder(o => o === 'asc' ? 'desc' : 'asc')}
                                className={clsx('p-2 rounded-lg hover:bg-slate-50 transition-colors',
                                    order === 'asc' ? 'text-teal-600' : 'text-slate-400')}
                                title={order === 'asc' ? 'Ascending' : 'Descending'}
                            >
                                <ArrowUpDown size={14} />
                            </button>
                            <button
                                onClick={() => refetch()}
                                className="p-2 rounded-lg hover:bg-slate-50 text-slate-400 transition-colors"
                                title="Refresh"
                            >
                                <RefreshCw size={14} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* ========== BUSINESS LIST ========== */}
                <div ref={listRef}>
                    {enrichedBusinesses.length === 0 ? (
                        <EmptyState
                            icon={<Building2 size={48} />}
                            title={search ? 'No businesses match your search' : 'No businesses yet'}
                            description={search ? 'Try a different search term.' : 'Create your first business to start organizing your operations.'}
                            action={search ? { label: 'Clear Search', onClick: () => setSearch('') } : { label: 'Add Business', onClick: () => setShowForm(true) }}
                        />
                    ) : viewMode === 'table' ? (
                        /* ========== TABLE VIEW ========== */
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50">
                                            <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Business</th>
                                            <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Type</th>
                                            <th className="px-6 py-4 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider">Branches</th>
                                            <th className="px-6 py-4 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Staff</th>
                                            <th className="px-6 py-4 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Created</th>
                                            <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {enrichedBusinesses.map((b) => {
                                            const typeConfig = businessTypes[b.type] || businessTypes.retail;
                                            const branchCount = (b as any).branchCount || 0;
                                            const staffCount = (b as any).staffCount || 0;
                                            return (
                                                <motion.tr
                                                    key={b.id}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    className={clsx(
                                                        'hover:bg-slate-50/50 group cursor-pointer transition-colors',
                                                        b.id === newBusinessId && 'bg-emerald-50/50'
                                                    )}
                                                    onClick={() => navigate(`/businesses/${b.id}`)}
                                                >
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div
                                                                className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-md flex-shrink-0"
                                                                style={{ backgroundColor: b.brandColor || '#0D9488' }}
                                                            >
                                                                {b.name.charAt(0).toUpperCase()}
                                                            </div>
                                                            <div className="min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <p className="text-sm font-semibold text-slate-900 truncate group-hover:text-teal-600 transition-colors">
                                                                        {b.name}
                                                                    </p>
                                                                    {b.id === newBusinessId && (
                                                                        <Badge className="bg-emerald-500 text-white text-[10px] px-1.5 py-0">New</Badge>
                                                                    )}
                                                                </div>
                                                                <code className="text-xs text-slate-400 font-mono">{b.shortCode}</code>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 hidden md:table-cell">
                                                        <Badge variant="neutral" size="sm" style={{ backgroundColor: `${typeConfig.color}15`, color: typeConfig.color, borderColor: `${typeConfig.color}30` }}>
                                                            {typeConfig.emoji} {typeConfig.label}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-sm font-semibold text-slate-700">{branchCount}</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center hidden sm:table-cell">
                                                        <span className="text-sm font-semibold text-slate-700">{staffCount}</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <Badge variant={b.isActive ? 'success' : 'danger'} size="sm">
                                                            {b.isActive ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-6 py-4 hidden lg:table-cell text-xs text-slate-500">
                                                        {formatTimeAgo(b.createdAt)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-all">
                                                            <button onClick={(e) => { e.stopPropagation(); navigate(`/businesses/${b.id}`); }} className="p-1.5 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-all" title="View">
                                                                <Eye size={14} />
                                                            </button>
                                                            <button onClick={(e) => { e.stopPropagation(); handleEdit(b); }} className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-all" title="Edit">
                                                                <Pencil size={14} />
                                                            </button>
                                                            <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(b); }} className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-all" title="Deactivate">
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </motion.tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={totalCount}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                            />
                        </div>
                    ) : (
                        /* ========== CARD VIEW ========== */
                        <div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {enrichedBusinesses.map((b) => (
                                    <BusinessCard
                                        key={b.id}
                                        business={b}
                                        onEdit={handleEdit}
                                        onDelete={setDeleteConfirm}
                                        isNew={b.id === newBusinessId}
                                    />
                                ))}
                            </div>
                            {totalPages > 1 && (
                                <div className="mt-4 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                    <Pagination
                                        currentPage={page}
                                        totalPages={totalPages}
                                        totalItems={totalCount}
                                        pageSize={pageSize}
                                        onPageChange={handlePageChange}
                                        onPageSizeChange={handlePageSizeChange}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ========== CREATE/EDIT MODAL ========== */}
                <AnimatePresence>
                    {showForm && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                            onClick={handleCloseForm}
                        >
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.95, opacity: 0 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
                            >
                                {/* Modal Header */}
                                <div className="sticky top-0 bg-white rounded-t-3xl z-10">
                                    <div className="px-8 py-5 bg-gradient-to-r from-teal-500 to-emerald-600 rounded-t-3xl">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-white/20">
                                                    {editingBusiness ? <Pencil size={20} className="text-white" /> : <Building2 size={20} className="text-white" />}
                                                </div>
                                                <div>
                                                    <h2 className="text-lg font-bold text-white">
                                                        {editingBusiness ? 'Edit Business' : 'Add New Business'}
                                                    </h2>
                                                    <p className="text-teal-100 text-xs">
                                                        {editingBusiness ? 'Update business details' : 'Set up a new business entity'}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleCloseForm}
                                                className="p-2 rounded-xl bg-white/20 text-white hover:bg-white/30 transition-colors"
                                            >
                                                <X size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-8 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input
                                            label="Business Name *"
                                            value={form.name}
                                            error={formErrors.name}
                                            onChange={e => updateFormField('name', e.target.value)}
                                            placeholder="e.g., Main Street Store"
                                            required
                                        />
                                        <Input
                                            label="Short Code *"
                                            value={form.shortCode}
                                            error={formErrors.shortCode}
                                            onChange={e => updateFormField('shortCode', e.target.value.toUpperCase())}
                                            placeholder="MSS"
                                            hint="2-20 chars, A-Z, 0-9 only"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Business Type</label>
                                        <select
                                            value={form.type}
                                            onChange={e => updateFormField('type', e.target.value)}
                                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 bg-white transition-all"
                                        >
                                            {Object.entries(businessTypes).map(([v, { emoji, label }]) => (
                                                <option key={v} value={v}>{emoji} {label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <Input
                                        label="Description"
                                        value={form.description}
                                        onChange={e => updateFormField('description', e.target.value)}
                                        placeholder="Brief description of this business"
                                    />

                                    <div className="grid grid-cols-2 gap-4">
                                        <Input
                                            label="Contact Email"
                                            type="email"
                                            value={form.contactEmail}
                                            error={formErrors.contactEmail}
                                            onChange={e => updateFormField('contactEmail', e.target.value)}
                                            placeholder="contact@business.com"
                                        />
                                        <Input
                                            label="Contact Phone"
                                            value={form.contactPhone}
                                            error={formErrors.contactPhone}
                                            onChange={e => updateFormField('contactPhone', e.target.value)}
                                            placeholder="+234..."
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <Input
                                            label="Website"
                                            value={form.website}
                                            error={formErrors.website}
                                            onChange={e => updateFormField('website', e.target.value)}
                                            placeholder="https://..."
                                        />
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Brand Color</label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="color"
                                                    value={form.brandColor}
                                                    onChange={e => updateFormField('brandColor', e.target.value)}
                                                    className="w-10 h-10 rounded-lg border-2 border-slate-200 cursor-pointer hover:scale-105 transition-transform"
                                                />
                                                <input
                                                    type="text"
                                                    value={form.brandColor}
                                                    onChange={e => updateFormField('brandColor', e.target.value)}
                                                    className="flex-1 px-3 py-3 border-2 border-slate-200 rounded-xl text-sm font-mono focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                                                    placeholder="#0D9488"
                                                />
                                            </div>
                                            {formErrors.brandColor && (
                                                <p className="text-xs text-rose-500 font-medium mt-1.5">{formErrors.brandColor}</p>
                                            )}
                                        </div>
                                    </div>

                                    <Input
                                        label="Address"
                                        value={form.address}
                                        onChange={e => updateFormField('address', e.target.value)}
                                        placeholder="Physical address"
                                    />
                                </div>

                                <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 rounded-b-3xl px-8 py-4 flex justify-end gap-3">
                                    <Button variant="secondary" onClick={handleCloseForm}>Cancel</Button>
                                    <Button
                                        onClick={() => { if (validateForm()) saveMutation.mutate(); }}
                                        isLoading={saveMutation.isPending}
                                        disabled={!form.name || !form.shortCode}
                                        leftIcon={editingBusiness ? <Save size={16} /> : <Rocket size={16} />}
                                    >
                                        {editingBusiness ? 'Save Changes' : 'Create Business'}
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ========== DELETE CONFIRMATION ========== */}
                <AnimatePresence>
                    {deleteConfirm && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                            onClick={() => setDeleteConfirm(null)}
                        >
                            <motion.div
                                initial={{ scale: 0.95 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.95 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 text-center"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
                                    <Trash2 size={28} className="text-rose-500" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900">Deactivate Business?</h3>
                                <p className="text-sm text-slate-500 mt-2">
                                    This will deactivate <strong>{deleteConfirm.name}</strong> and hide it from active views. You can reactivate it later.
                                </p>
                                <div className="flex gap-3 mt-8">
                                    <Button variant="secondary" onClick={() => setDeleteConfirm(null)} className="flex-1">Cancel</Button>
                                    <Button
                                        variant="danger"
                                        onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                                        isLoading={deleteMutation.isPending}
                                        className="flex-1"
                                    >
                                        Deactivate
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}